import React from "react";
import {Ticket} from "@lib/Contact/ContactModels";
import {User} from "@lib/User/UserModels";
import {AppModalConfig, useModalStore} from "@store/ModalStore";
import {useProjectTranslations} from "@i18n/i18nUtils";
import "./Unsubscribe.scss";
import ContactService from "@lib/Contact/ContactService";

export const UNSUBSCRIBE_MODAL_KEY = "unsubscribe-program";

export const UNSUBSCRIBE_REASON = "GWS_GENERAL";
export const UNSUBSCRIBE_SPECIFIC_REASON = "GWS_UNSUB";

export const UNSUBSCRIBE_GENERIC_MESSAGE =
    "L'utente ha richiesto di disattivare il suo account tramite il link presente nel footer. Questo è un messaggio automatico.";

export const UNSUBSCRIBE_PROCESSING_DAYS = 5;

export function buildUnsubscribeTicket(user: User): Ticket {
    return {
        requester: {
            name: user?.name?.trim() ?? "",
            surname: user?.surname?.trim() ?? "",
            email: user?.email?.trim() ?? "",
        },
        ticketBody: {
            message: UNSUBSCRIBE_GENERIC_MESSAGE,
            reason: UNSUBSCRIBE_REASON,
            secondaryReason: UNSUBSCRIBE_SPECIFIC_REASON,
            orderId: undefined,
            files: [],
        },
    };
}

export function canSubmitUnsubscribeTicket(user: User): boolean {
    return !!user && !!user?.name?.trim() && !!user?.surname?.trim() && !!user?.email?.trim();
}

type UnsubscribeModalContentProps = {
    body: string;
    finePrint?: string;
};

export default function UnsubscribeModalContent({
                                                    body,
                                                    finePrint,
                                                }: UnsubscribeModalContentProps) {
    return (
        <div className="unsubscribeModalContent">
            <p className="tp-sm semibold">{body}</p>
            {finePrint ? (
                <p className="tp-xs light color-primary unsubscribeModalContent__finePrint">
                    {finePrint}
                </p>
            ) : null}
        </div>
    );
}

type TFunctionLike = (key: string, options?: Record<string, any>) => string;

type OpenUnsubscribeModalParams = {
    t: TFunctionLike;
    user: User;
};

function makeErrorFooterMessage(t: TFunctionLike) {
    return (
        <p className="tp-2xs color-primary unsubscribeModalContent__feedback">
            {t("unsubscribe:modal:error:text")}
        </p>
    );
}

export async function openUnsubscribeModal({
                                               t,
                                               user,
                                           }: OpenUnsubscribeModalParams) {
    const {open, update} = useModalStore.getState();

    const canSubmit = canSubmitUnsubscribeTicket(user);
    const modalKey = UNSUBSCRIBE_MODAL_KEY;

    const submit = async () => {
        if (!canSubmit) return;

        update(
            {
                tone: "warning",
                title: t("unsubscribe:modal:title"),
                content: (
                    <UnsubscribeModalContent
                        body={t("unsubscribe:modal:text")}
                    />
                ),
                footerMessage: undefined,
                isDismissable: false,
                showCloseButton: false,
                secondaryAction: {
                    label: t("close"),
                    autoClose: false,
                },
                primaryAction: {
                    label: t("loading"),
                    disabled: true,
                    autoClose: false,
                },
            },
            modalKey
        );

        try {
            const payload = buildUnsubscribeTicket(user);
            await ContactService.postTicket(payload);

            update(
                {
                    tone: "success",
                    title: t("unsubscribe:modal:success:title"),
                    content: (
                        <UnsubscribeModalContent
                            body={t("unsubscribe:modal:success:text")}
                        />
                    ),
                    footerMessage: undefined,
                    isDismissable: true,
                    showCloseButton: true,
                    secondaryAction: undefined,
                    primaryAction: {
                        label: t("unsubscribe:modal:success:label"),
                        autoClose: true,
                    },
                },
                modalKey
            );
        } catch (error) {
            console.error("[unsubscribe] postTicket failed", error);

            update(
                {
                    tone: "warning",
                    title: t("unsubscribe:modal:title"),
                    content: (
                        <UnsubscribeModalContent
                            body={t("unsubscribe:modal:text")}
                        />
                    ),
                    footerMessage: makeErrorFooterMessage(t),
                    isDismissable: true,
                    showCloseButton: true,
                    secondaryAction: {
                        label: t("close"),
                        autoClose: true,
                    },
                    primaryAction: {
                        label: t("unsubscribe:modal:cta"),
                        disabled: false,
                        autoClose: false,
                        onPress: submit,
                    },
                },
                modalKey
            );
        }
    };

    const config: AppModalConfig = {
        key: modalKey,
        tone: "warning",
        title: t("unsubscribe:modal:title"),
        content: (
            <UnsubscribeModalContent
                body={t("unsubscribe:modal:text")}
            />
        ),
        footerMessage: undefined,
        isDismissable: true,
        showCloseButton: true,
        secondaryAction: {
            label: t("close"),
        },
        primaryAction: {
            label: t("unsubscribe:modal:cta"),
            disabled: !canSubmit,
            autoClose: false,
            onPress: submit,
        },
    };

    await open(config);
}

interface FooterUnsubscribeLinkProps {
    user: User;
}

export function FooterUnsubscribeLink({user}: FooterUnsubscribeLinkProps) {
    const {t} = useProjectTranslations();

    const handleClick = async () => {
        await openUnsubscribeModal({
            t,
            user,
        });
    };

    return (
        <button
            type="button"
            className="footerUnsubscribeLink tp-xs"
            onClick={handleClick}
        >
            {t("footer:unsubscribe_program")}
        </button>
    );
}